import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FadeLoader } from 'react-spinners';
import clsx from 'clsx';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { getRefundDetail, postRefundPayment } from 'api/fullsteam';
import { styledTheme } from 'globalStyles/theme';
import APFormGroup from 'components/FormFields/APFormGroup';
import InputField from 'components/FormFields/InputField';
import ConfirmModal from './ConfirmModal';
import PaymentModal from './PaymentModal';
import './styles.scss';

dayjs.extend(utc);

const REFUND_DETAIL_DEFAULT = {
  identifier: '',
  name: '',
  original_amount: 0,
  max_refund_amount: 0,
  paid_by: '',
  paid_on: '',
  payment_method: '',
  logo_url: null,
};

const PaymentRefund = () => {
  const history = useHistory();
  const pathname = history.location?.pathname;
  const inputParams = pathname.replace('/app/v2/invoice/', '').split('/');
  const orgId = inputParams[0];
  const invoiceTokenId = inputParams[3];
  const paymentId = inputParams[4];
  const [refundDetail, setRefundDetail] = useState(REFUND_DETAIL_DEFAULT);
  const [amount, setRefund] = useState(0);
  const [amountErr, setAmountError] = useState('');
  const [reason, setReason] = useState('');
  const [refundError, setRefundError] = useState('');
  const [isEmpty, setIsEmpty] = useState(true);

  const [isFormLoaded, setFormLoaded] = useState(false);
  const [isProcessing, setProcessing] = useState(false);
  const [isRefunded, setRefundStatus] = useState(false);

  useEffect(() => {
    const handleRefundDetail = async () => {
      const detailResponse = await getRefundDetail({ invoiceId: invoiceTokenId, paymentId });
      setFormLoaded(true);
      if (detailResponse?.status === 200) {
        const {
          invoice_name,
          invoice_identifier,
          net_total_amount,
          paid_by,
          paid_on,
          logo,
          account_last_4,
          processor_type = '',
        } = detailResponse.data;
        setRefundDetail({
          invoice_name,
          invoice_identifier,
          paid_by,
          paid_on: paid_on ? dayjs(paid_on).utc(true).local().format('MM/DD/YYYY') : '',
          logo_url: logo?.full?.url || null,
          max_refund_amount: net_total_amount,
          payment_method:
            ['ACH', 'EFT'].includes(processor_type) ?
              `ACH/EFT Ending in ${account_last_4}` :
              `${processor_type} Ending in ${account_last_4}`,
        });
        setRefund(net_total_amount);
        if (net_total_amount && net_total_amount > 0) {
          setIsEmpty(false);
        }
      } else {
        const errResponse = detailResponse.data;
        if ( errResponse && errResponse.PAYMENT_NOT_REFUNDABLE) {
          setRefundError(errResponse.PAYMENT_NOT_REFUNDABLE);
        }
      }
    };

    if (invoiceTokenId) handleRefundDetail();
  }, [invoiceTokenId]);

  const validateForm = () => {
    const amountValue = parseFloat(amount);
    if (refundDetail.max_refund_amount) {
      if ((amountValue <= 0) || (amountValue > refundDetail.max_refund_amount)) {
        setAmountError(`Amount value must be between 0.01 and ${refundDetail.max_refund_amount}`);
        return false;
      }
      setAmountError('');
      return true;
    }
    setAmountError('Original Amount value must not be 0');
    return false;
  };

  const onContinueClick = async () => {
    if (validateForm()) {
      let body = {
        total_amount_to_reverse: amount,
      };

      if (reason) {
        body = { ...body, refund_reason: reason };
      }

      setProcessing(true);
      const refundResponse =  await postRefundPayment({
        invoiceId: invoiceTokenId,
        paymentId,
        body,
      });
      setProcessing(false);

      if (refundResponse?.status === 200) {
        setRefundStatus(true);
      } else if (refundResponse?.status === 404) {
        setRefundStatus(false);
        setRefundError('Transaction failed by our server.');
      } else {
        setRefundStatus(false);
        try {
          const errResponse = refundResponse.data;
          if ( errResponse && errResponse.TRANSACTION_FAILED) {
            setRefundError(errResponse.TRANSACTION_FAILED);
          } else {
            setRefundError('Transaction declined by issuer');
          }
        } catch(e) {
          setRefundError('Transaction declined by issuer');
        }
      }
    } else {
      setRefundError('We cannot proceed your refund request. Please fix the errors and try again.');
    }
  };

  return (
    <>
      <PaymentModal
        invoiceId={invoiceTokenId}
        opacity={isProcessing ? 0.8 : 1}
        onClose={() => {
          window.location.href = `/app/pro/${orgId}/invoices/payments`;
        }}
      >
        {isProcessing && (
          <section className="app_loader">
            <FadeLoader
              color={styledTheme.lighterGray}
              loading
              height={15}
              width={5}
              radius={5}
              margin={2}
            />
          </section>
        )}
        {!isFormLoaded && (
          <section className="app_loader">
            <FadeLoader
              color={styledTheme.lighterGray}
              loading
              height={15}
              width={5}
              radius={5}
              margin={2}
            />
          </section>
        )}
        <div className={`body-content container--small ${clsx( !isFormLoaded && 'form-blured' )}`}>
          {refundDetail.logo_url && (
            <h3 className="invoice-modal__title">
              <img
                src={refundDetail.logo_url}
                alt="Aisle Planner"
                className="ap-logo"
              />
            </h3>
          )}
          <div className="refund-intro">
            Refunds may take 5-10 days to appear on a customer's statement. Customers will be refunded the
            amount of the original payment, not including any Platform fees if applied. Platform fees are
            yours to keep and help offset the cost of accepting online payments since Wedpay's processing fees
            will not be refunded to you as the merchant. {' '}
            <a
              target="_blank"
              rel="noreferrer"
              href="https://help.aisleplanner.com/en/articles/2009831-issuing-refunds#h_f00f97e773"
            >
              <span>Learn more</span>
            </a>.
          </div>
          <div className="form-group invoice-detail mb-3">
            <div className="invoice-detail__header">
              <span>Invoice #{refundDetail.invoice_identifier}</span>
              <span>Invoice Name: {refundDetail.invoice_name}</span>
            </div>
            <div className="invoice-detail__content">
              <div className="invoice-detail__item invoice-detail__item--row">
                <span className="dashline" />
                <span className="info pr-2">Original Payment</span>
                <span className="info pl-2">${refundDetail?.max_refund_amount}</span>
              </div>
              <div className="invoice-detail__item invoice-detail__item--row">
                <span className="dashline" />
                <span className="info pr-2">Paid By</span>
                <span className="info pl-2">{refundDetail?.paid_by}</span>
              </div>
              <div className="invoice-detail__item invoice-detail__item--row">
                <span className="dashline" />
                <span className="info pr-2">Paid On</span>
                <span className="info pl-2">{refundDetail?.paid_on}</span>
              </div>
              <div className="invoice-detail__item invoice-detail__item--row">
                <span className="dashline" />
                <span className="info pr-2">Payment Method</span>
                <span className="info pl-2">{refundDetail?.payment_method}</span>
              </div>
            </div>
          </div>
          <APFormGroup
            className="form-group--refund"
            grid={false}
          >
            <InputField
              id="amount"
              name="amount"
              type="number"
              label="Refund Amount"
              className={`form-control ${clsx( amountErr !== '' && 'input-error' )}`}
              title="Please input a valid amount"
              value={Number.isNaN(amount) ? '' : amount}
              min="0.01"
              data-val="true"
              data-val-min="The refund amount must be larger than 0"
              data-val-max="The refund amount must be equal or lower than the original balance."
              required
              disabled={!refundDetail.max_refund_amount}
              max={refundDetail.balance}
              onChange={(e) => {
                setAmountError('');
                const amountValue = parseFloat(e.target.value);
                setRefund(parseInt(amountValue * 100, 10)/100);
                if (Number.isNaN(amountValue) || amountValue === '') {
                  setIsEmpty(true);
                }
                else {
                  setIsEmpty(false);
                }
              }}
            />
            <span className="text-danger field-validation-valid"
              data-valmsg-for="amount"
              data-valmsg-replace="true"
            >
              {amountErr}
            </span>
          </APFormGroup>
          <APFormGroup
            className="form-group--refund"
            grid={false}
          >
            <InputField
              label="Reason for Refund"
              className="form-control"
              data-val="true"
              id="reason"
              name="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <span className="refund-error text-danger field-validation-valid">
              {refundError}
            </span>
          </APFormGroup>
          <div className="form-group text-center">
            <input
              type="button"
              value={`Refund $${Number.isNaN(amount) ? '' : amount} Now`}
              className="submit_button button button--lg"
              onClick={onContinueClick}
              disabled={!!amountErr || isEmpty || isProcessing || !isFormLoaded}
            />
          </div>
        </div>
      </PaymentModal>
      <ConfirmModal
        open={isRefunded}
        onClose={() => {
          window.location.href = `/app/pro/${orgId}/invoices/payments`;
        }}
        modalName="refund-issued"
        header
      >
        <div className="body-content container--small">
          <div className="row">
            <div className="col-md-12">
              <div className="form-group text-center text-normal">
                <h3>Your refund has been issued.</h3>
                <p className="mt-4">
                  A refund in the amount of ${amount} has been issued to {refundDetail?.paid_by} and can expect
                  to see this refund appear in their account in 5-10 business days. You'll now see this refund
                  applied to the invoice.
                </p>
                <input
                  type="button"
                  value="Return To Payments"
                  className="submit_button button button--lg mt-5"
                  onClick={() => {
                    window.location.href = `/app/pro/${orgId}/invoices/payments`;
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </ConfirmModal>
    </>
  );
};

PaymentRefund.propTypes = {
};

PaymentRefund.defaultProps = {
};

export default PaymentRefund;
